import { Entity } from '@core/api/entity';

import { ClientDetailsReadDTO, ClientDetailsWriteDTO, CountryValueDTO } from '@core/dto/client-details.dto';
import { ContactTypeDTO } from '@core/dto/contact.dto';
import { ValueObjectDTO } from '@core/dto/value-object.dto';
import { Field, Model, Serializer } from '@junte/serialize-ts';
import { ClientDetailsInvestorTypeDTO, ClientDetailsStatusDTO, ClientDetailsTypeDTO } from '../dto/client-details.dto';
import { ContactCopy } from './contacts-copy.model';
import { FileObject, FileObjectSerializer } from './file-object.model';
import { ValueObject, ValueObjectSerializer } from './value-object.model';

export type ClientDetailsInvestorType = ClientDetailsInvestorTypeDTO;
export type ClientDetailsType = ClientDetailsTypeDTO;
export type ClientDetailsStatus = ClientDetailsStatusDTO;

export interface CountryValue {
  codename: string;
  name: string;
}

export interface OnboardingClassificationReason {
  code?: string;
  label?: string;
  title?: string;
  details?: string | Record<string, unknown>;
  impact?: string;
  result?: string;
  description?: string;
  status?: string;
  met?: boolean;
}

export interface OnboardingQuestionStatus {
  status?: string;
  completed?: boolean;
  completedAt?: string | null;
}

export interface OnboardingClientQuestions {
  understandRisk?: OnboardingQuestionStatus;
  understandRiskClosedFunds?: OnboardingQuestionStatus;
  overallStatus?: string;
  overallCompleted?: boolean;
}

export interface OnboardingWaiverSummary {
  applicable?: boolean;
  type?: string;
  status?: string;
  message?: string;
  completedAt?: string | null;
}

export interface OnboardingIdentificationSummary {
  provider?: string;
  status?: string;
  completed?: boolean;
  completedAt?: string | null;
}

type ContactPrefix = 'benefOwner' | 'legalRepr';
type ContactSuffix =
  | 'FirstName'
  | 'IsPep'
  | 'LastName'
  | 'Address'
  | 'Email'
  | 'Mobile'
  | 'Phone'
  | 'Salutation'
  | 'Title'
  | 'SignedDeclaration'
  | 'IdCardScan'
  | 'Delete';

export class ClientDetailsSerializer implements Serializer<ClientDetails> {
  public serialize(model: ClientDetails): ClientDetailsWriteDTO {
    const vos = new ValueObjectSerializer();

    const dto: ClientDetailsWriteDTO = {
      uid: model.id,
      companyNumber: model.companyNumber,
      companyName: model.companyName,
      // address: model.address,
      addressSameAsCorrespondence: model.addressSameAsCorrespondence,
      addressCity: model.addressCity,
      addressStreet: model.addressStreet,
      addressPostalCode: model.addressPostalCode,
      addressCountry: model.addressCountry ? vos.serialize(model.addressCountry) : model.addressCountry,
      correspondenceAddressCity: model.correspondenceAddressCity,
      correspondenceAddressStreet: model.correspondenceAddressStreet,
      correspondenceAddressPostalCode: model.correspondenceAddressPostalCode,
      correspondenceAddressCountry: model.correspondenceAddressCountry ? vos.serialize(model.correspondenceAddressCountry) : model.correspondenceAddressCountry,
      hasUserLoggedIn: model.hasUserLoggedIn,
      hasUserInvited: model.hasUserInvited,
      website: model.website,
      totalBalanceSheet: model.totalBalanceSheet,
      netTurnover: model.netTurnover,
      ownFunds: model.ownFunds,
      title: model.title ? vos.serialize(model.title) : model.title,
      salutation: model.salutation ? vos.serialize(model.salutation) : model.salutation,
      placeOfBirth: model.placeOfBirth,
      dateOfBirth: model.dateOfBirth,
      cityOfBirth: model.cityOfBirth,
      mobileNumber: model.mobileNumber,
      email: model.email,
      linkedin: model.linkedin,
      bankName: model.bankName,
      bankAccountName: model.bankAccountName,
      bankIban: model.bankIban,
      bic: model.bic,
      bankCurrency: model.bankCurrency,
      taxResident: model.taxResident,
      taxIdentificationNumber: model.taxIdentificationNumber,
      taxResidentOther: model.taxResidentOther,
      taxIdentificationNumberOther: model.taxIdentificationNumberOther,
      politicallyExposed: model.politicallyExposed,
      politicallyExposedText: model.politicallyExposedText,
      mainSourceOfFunds: model.mainSourceOfFunds ? vos.serialize(model.mainSourceOfFunds) : model.mainSourceOfFunds,
      cashIntensiveTransactions: model.cashIntensiveTransactions,
      oilArmTransaction: model.oilArmTransaction,
      cryptoAssets: model.cryptoAssets,
      understandRiskClosedFunds: model.understandRiskClosedFunds,
      understandRisk: model.understandRisk,
      noUsaResident: model.noUsaResident,
      bornUsa: model.bornUsa,
      notBornUsa: model.notBornUsa,
      investorClientAdviceLicence: model.investorClientAdviceLicence,
      firstname: model.firstname,
      surname: model.surname,
      waiverAgree: model.waiverAgree,
      waiverAgreeSemi: model.waiverAgreeSemi,
      soleRepresentation: model.soleRepresentation,
      listedStockExchange: model.listedStockExchange,
      acquiringInvestment: model.acquiringInvestment,
      ownBehalf: model.ownBehalf,
      investmentObjectiveGeneral: model.investmentObjectiveGeneral,
      investmentObjectiveRetirement: model.investmentObjectiveRetirement,
      investmentObjectiveDivers: model.investmentObjectiveDivers,
      investmentObjectiveOther: model.investmentObjectiveOther,
      purposeOfReturns: model.purposeOfReturns,
      commercialRegisterExtract: model.commercialRegisterExtract,
      articlesOfAssocOrContract: model.articlesOfAssocOrContract,
      lastAnnualFinStatement: model.lastAnnualFinStatement,
      transparencyRegistry: model.transparencyRegistry,
      structureChart: model.structureChart,
      bankStatement: model.bankStatement,
      legalReprSignedDeclaration: model.legalReprSignedDeclaration,
      legalReprIdCardScan: model.legalReprIdCardScan,
      benefOwnerSignedDeclaration: model.benefOwnerSignedDeclaration,
      benefOwnerIdCardScan: model.benefOwnerIdCardScan,
      proofResidentialAddress: model.proofResidentialAddress,
      proofSourceOfFunds: model.proofSourceOfFunds,
      // Proof of funds variants
      ...(model as any).proofOfFundsBankStatement && { proofOfFundsBankStatement: (model as any).proofOfFundsBankStatement },
      ...(model as any).proofOfFundsInheritance && { proofOfFundsInheritance: (model as any).proofOfFundsInheritance },
      ...(model as any).proofOfFundsPropertySale && { proofOfFundsPropertySale: (model as any).proofOfFundsPropertySale },
      ...(model as any).proofOfFundsCompanySale && { proofOfFundsCompanySale: (model as any).proofOfFundsCompanySale },
      ...(model as any).proofOfFundsInvestment && { proofOfFundsInvestment: (model as any).proofOfFundsInvestment },
      ...(model as any).proofOfFundsSavings && { proofOfFundsSavings: (model as any).proofOfFundsSavings },
      legalReprNum: model.legalRepresentatives?.length,
      benefOwnerNum: model.beneficialOwners?.length,
    };

    let flatLegalRepresentatives: {} = null;
    let flatBeneficalOwners: {} = null;
    let deletedLegalRepresentatives: {} = null;
    let deletedBeneficalOwners: {} = null;

    if (model.legalRepresentatives) {
      flatLegalRepresentatives = flattenContacts('legalRepr', model.legalRepresentatives ?? []);
      deletedLegalRepresentatives = buildDeletedContacts(
        'legalRepr',
        model.legalRepresentatives?.length ?? 0,
        model.legalRepresentativesBackendCount ?? 0,
      );
    }

    if (model.beneficialOwners) {
      flatBeneficalOwners = flattenContacts('benefOwner', model.beneficialOwners ?? []);
      deletedBeneficalOwners = buildDeletedContacts(
        'benefOwner',
        model.beneficialOwners?.length ?? 0,
        model.beneficialOwnersBackendCount ?? 0,
      );
    }

    const image: {} = null;

    return Object.assign(
      dto,
      flatLegalRepresentatives,
      flatBeneficalOwners,
      deletedLegalRepresentatives,
      deletedBeneficalOwners,
      image,
    ) as ClientDetailsWriteDTO;
  }

  public deserialize(dto: ClientDetailsReadDTO): ClientDetails {
    const fos = new FileObjectSerializer();
    return ClientDetails.create({
      companyNumber: dto.companyNumber,
      companyName: dto.companyName,
      addressSameAsCorrespondence: dto.addressSameAsCorrespondence,
      addressCity: dto.addressCity,
      addressStreet: dto.addressStreet,
      addressPostalCode: dto.addressPostalCode,
      addressCountry: dto.addressCountry ? ValueObject.create(dto.addressCountry) : null,
      correspondenceAddressCity: dto.correspondenceAddressCity,
      correspondenceAddressStreet: dto.correspondenceAddressStreet,
      correspondenceAddressPostalCode: dto.correspondenceAddressPostalCode,
      correspondenceAddressCountry: dto.correspondenceAddressCountry
        ? ValueObject.create(dto.correspondenceAddressCountry)
        : null,
      hasUserLoggedIn: dto.hasUserLoggedIn,
      hasUserInvited: dto.hasUserInvited,
      website: dto.website,
      totalBalanceSheet: dto.totalBalanceSheet,
      netTurnover: dto.netTurnover,
      ownFunds: dto.ownFunds,
      title: dto.title ? ValueObject.create(dto.title) : null,
      salutation: dto.salutation ? ValueObject.create(dto.salutation) : null,
      placeOfBirth: dto.placeOfBirth,
      dateOfBirth: dto.dateOfBirth,
      cityOfBirth: dto.cityOfBirth,
      mobileNumber: dto.mobileNumber,
      email: dto.email,
      linkedin: dto.linkedin,
      bankName: dto.bankName,
      bankAccountName: dto.bankAccountName,
      bankIban: dto.bankIban,
      bic: dto.bic,
      bankCurrency: dto.bankCurrency as string,
      taxResident: dto.taxResident,
      taxIdentificationNumber: dto.taxIdentificationNumber,
      taxResidentOther: dto.taxResidentOther,
      taxIdentificationNumberOther: dto.taxIdentificationNumberOther,
      politicallyExposed: dto.politicallyExposed,
      politicallyExposedText: dto.politicallyExposedText,
      mainSourceOfFunds: dto.mainSourceOfFunds ? ValueObject.create(dto.mainSourceOfFunds) : null,
      cashIntensiveTransactions: dto.cashIntensiveTransactions,
      oilArmTransaction: dto.oilArmTransaction,
      cryptoAssets: dto.cryptoAssets,
      understandRiskClosedFunds: dto.understandRiskClosedFunds,
      understandRisk: dto.understandRisk,
      noUsaResident: dto.noUsaResident,
      bornUsa: dto.bornUsa,
      notBornUsa: dto.notBornUsa,
      investorClientAdviceLicence: dto.investorClientAdviceLicence,
      firstname: dto.firstname,
      surname: dto.surname,
      waiverAgree: dto.waiverAgree,
      waiverAgreeSemi: dto.waiverAgreeSemi,
      soleRepresentation: dto.soleRepresentation,
      listedStockExchange: dto.listedStockExchange,
      beneficialOwners: dto.benefOwnerNum ? unflattenContacts('benefOwner', dto, dto.benefOwnerNum) : null,
      beneficialOwnersBackendCount: dto.benefOwnerNum ?? 0,
      acquiringInvestment: dto.acquiringInvestment,
      ownBehalf: dto.ownBehalf,
      investmentObjectiveGeneral: dto.investmentObjectiveGeneral,
      investmentObjectiveRetirement: dto.investmentObjectiveRetirement,
      investmentObjectiveDivers: dto.investmentObjectiveDivers,
      investmentObjectiveOther: dto.investmentObjectiveOther,
      purposeOfReturns: dto.purposeOfReturns,
      commercialRegisterExtract: dto.commercialRegisterExtract ? fos.deserialize(dto.commercialRegisterExtract) : null,
      articlesOfAssocOrContract: dto.articlesOfAssocOrContract ? fos.deserialize(dto.articlesOfAssocOrContract) : null,
      lastAnnualFinStatement: dto.lastAnnualFinStatement ? fos.deserialize(dto.lastAnnualFinStatement) : null,
      transparencyRegistry: dto.transparencyRegistry ? fos.deserialize(dto.transparencyRegistry) : null,
      structureChart: dto.structureChart ? fos.deserialize(dto.structureChart) : null,
      bankStatement: dto.bankStatement ? fos.deserialize(dto.bankStatement) : null,
      legalReprSignedDeclaration: dto.legalReprSignedDeclaration
        ? fos.deserialize(dto.legalReprSignedDeclaration)
        : null,
      legalReprIdCardScan: dto.legalReprIdCardScan ? fos.deserialize(dto.legalReprIdCardScan) : null,
      benefOwnerSignedDeclaration: dto.benefOwnerSignedDeclaration
        ? fos.deserialize(dto.benefOwnerSignedDeclaration)
        : null,
      benefOwnerIdCardScan: dto.benefOwnerIdCardScan ? fos.deserialize(dto.benefOwnerIdCardScan) : null,
      proofResidentialAddress: dto.proofResidentialAddress ? fos.deserialize(dto.proofResidentialAddress) : null,
      proofSourceOfFunds: dto.proofSourceOfFunds ? fos.deserialize(dto.proofSourceOfFunds) : null,
      // Proof of funds variants
      proofOfFundsBankStatement: (dto as any).proofOfFundsBankStatement ? fos.deserialize((dto as any).proofOfFundsBankStatement) : null,
      proofOfFundsInheritance: (dto as any).proofOfFundsInheritance ? fos.deserialize((dto as any).proofOfFundsInheritance) : null,
      proofOfFundsPropertySale: (dto as any).proofOfFundsPropertySale ? fos.deserialize((dto as any).proofOfFundsPropertySale) : null,
      proofOfFundsCompanySale: (dto as any).proofOfFundsCompanySale ? fos.deserialize((dto as any).proofOfFundsCompanySale) : null,
      proofOfFundsInvestment: (dto as any).proofOfFundsInvestment ? fos.deserialize((dto as any).proofOfFundsInvestment) : null,
      proofOfFundsSavings: (dto as any).proofOfFundsSavings ? fos.deserialize((dto as any).proofOfFundsSavings) : null,
      legalRepresentatives: unflattenContacts('legalRepr', dto, dto.legalReprNum),
      legalRepresentativesBackendCount: dto.legalReprNum ?? 0,
      classificationReasons: getClassificationReasons(dto),
      clientQuestions: getClientQuestions(dto),
      waiverSummary: getWaiverSummary(dto),
      identificationSummary: getIdentificationSummary(dto),
    });
  }
}

@Model(new ClientDetailsSerializer())
export class ClientDetails extends Entity {
  @Field({ serializer: new ClientDetailsSerializer() })
  companyNumber: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  companyName: string;

  // @Field()
  // address: ValueObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  addressCity: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  addressStreet: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  addressPostalCode: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  addressSameAsCorrespondence: boolean;

  @Field({ serializer: new ClientDetailsSerializer() })
  addressCountry: ValueObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  correspondenceAddressCity: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  correspondenceAddressStreet: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  correspondenceAddressPostalCode: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  correspondenceAddressCountry: ValueObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  hasUserLoggedIn: boolean;

  @Field({ serializer: new ClientDetailsSerializer() })
  hasUserInvited: boolean;

  @Field({ serializer: new ClientDetailsSerializer() })
  website: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  totalBalanceSheet: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  netTurnover: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  ownFunds: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  title: ValueObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  salutation: ValueObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  placeOfBirth: CountryValueDTO;

  @Field({ serializer: new ClientDetailsSerializer() })
  @Field()
  dateOfBirth: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  cityOfBirth: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  mobileNumber: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  email: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  linkedin: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  bankName: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  bankAccountName: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  bankIban: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  bic: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  bankCurrency: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  taxResident: CountryValueDTO;

  @Field({ serializer: new ClientDetailsSerializer() })
  taxIdentificationNumber: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  taxResidentOther: CountryValueDTO;

  @Field({ serializer: new ClientDetailsSerializer() })
  taxIdentificationNumberOther: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  politicallyExposed: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  politicallyExposedText: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  mainSourceOfFunds: ValueObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  cashIntensiveTransactions: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  oilArmTransaction: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  cryptoAssets: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  understandRiskClosedFunds: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  understandRisk: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  noUsaResident: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  bornUsa: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  notBornUsa: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  investorClientAdviceLicence: boolean;

  @Field({ serializer: new ClientDetailsSerializer() })
  firstname: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  surname: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  waiverAgree: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  waiverAgreeSemi: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  soleRepresentation: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  listedStockExchange: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  beneficialOwner: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  acquiringInvestment: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  ownBehalf: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  investmentObjectiveGeneral: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  investmentObjectiveRetirement: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  investmentObjectiveDivers: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  investmentObjectiveOther: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  purposeOfReturns: string;

  @Field({ serializer: new ClientDetailsSerializer() })
  commercialRegisterExtract: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  articlesOfAssocOrContract: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  lastAnnualFinStatement: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  transparencyRegistry: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  structureChart: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  bankStatement: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  legalReprSignedDeclaration: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  legalReprIdCardScan: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  benefOwnerSignedDeclaration: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  benefOwnerIdCardScan: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  proofResidentialAddress: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  proofSourceOfFunds: FileObject;

  // Optional proof of funds variant files
  @Field({ serializer: new ClientDetailsSerializer() })
  proofOfFundsBankStatement: FileObject;
  @Field({ serializer: new ClientDetailsSerializer() })
  proofOfFundsInheritance: FileObject;
  @Field({ serializer: new ClientDetailsSerializer() })
  proofOfFundsPropertySale: FileObject;
  @Field({ serializer: new ClientDetailsSerializer() })
  proofOfFundsCompanySale: FileObject;
  @Field({ serializer: new ClientDetailsSerializer() })
  proofOfFundsInvestment: FileObject;
  @Field({ serializer: new ClientDetailsSerializer() })
  proofOfFundsSavings: FileObject;

  @Field({ serializer: new ClientDetailsSerializer() })
  legalRepresentatives: ContactCopy[];

  @Field({ serializer: new ClientDetailsSerializer() })
  beneficialOwners: ContactCopy[];

  @Field({ serializer: new ClientDetailsSerializer() })
  legalRepresentativesBackendCount: number;

  @Field({ serializer: new ClientDetailsSerializer() })
  beneficialOwnersBackendCount: number;

  @Field({ serializer: new ClientDetailsSerializer() })
  classificationReasons: OnboardingClassificationReason[];

  @Field({ serializer: new ClientDetailsSerializer() })
  clientQuestions: OnboardingClientQuestions;

  @Field({ serializer: new ClientDetailsSerializer() })
  waiverSummary: OnboardingWaiverSummary;

  @Field({ serializer: new ClientDetailsSerializer() })
  identificationSummary: OnboardingIdentificationSummary;

  public static create(data: Partial<ClientDetails>): ClientDetails {
    return Object.assign(new ClientDetails(), data);
  }

  public static createEmpty(): ClientDetails {
    return Object.assign(new ClientDetails(), {
      ompanyNumber: undefined,
      companyName: undefined,
      address: undefined,
      addressCity: undefined,
      addressStreet: undefined,
      addressPostalCode: undefined,
      addressCountry: undefined,
      website: undefined,
      totalBalanceSheet: undefined,
      netTurnover: undefined,
      ownFunds: undefined,
      title: undefined,
      salutation: undefined,
      placeOfBirth: undefined,
      dateOfBirth: undefined,
      cityOfBirth: undefined,
      mobileNumber: undefined,
      email: undefined,
      linkedin: undefined,
      bankName: undefined,
      bankAccountName: undefined,
      bankIban: undefined,
      bic: undefined,
      taxResident: undefined,
      taxIdentificationNumber: undefined,
      taxResidentOther: undefined,
      taxIdentificationNumberOther: undefined,
      politicallyExposed: undefined,
      politicallyExposedText: undefined,
      mainSourceOfFunds: undefined,
      cashIntensiveTransactions: undefined,
      oilArmTransaction: undefined,
      cryptoAssets: undefined,
      understandRiskClosedFunds: undefined,
      understandRisk: undefined,
      noUsaResident: undefined,
      bornUsa: undefined,
      notBornUsa: undefined,
      investorClientAdviceLicence: undefined,
      firstname: undefined,
      surname: undefined,
      waiverAgree: undefined,
      waiverAgreeSemi: undefined,
      soleRepresentation: undefined,
      listedStockExchange: undefined,
      beneficialOwner: undefined,
      acquiringInvestment: undefined,
      ownBehalf: undefined,
      investmentObjectiveGeneral: undefined,
      investmentObjectiveRetirement: undefined,
      investmentObjectiveDivers: undefined,
      investmentObjectiveOther: undefined,
      purposeOfReturns: undefined,
      commercialRegisterExtract: undefined,
      articlesOfAssocOrContract: undefined,
      lastAnnualFinStatement: undefined,
      transparencyRegistry: undefined,
      structureChart: undefined,
      bankStatement: undefined,
      legalReprSignedDeclaration: undefined,
      legalReprIdCardScan: undefined,
      benefOwnerSignedDeclaration: undefined,
      benefOwnerIdCardScan: undefined,
      proofResidentialAddress: undefined,
      proofSourceOfFunds: undefined,
      proofOfFundsBankStatement: undefined,
      proofOfFundsInheritance: undefined,
      proofOfFundsPropertySale: undefined,
      proofOfFundsCompanySale: undefined,
      proofOfFundsInvestment: undefined,
      proofOfFundsSavings: undefined,
      beneficialOwnersBackendCount: undefined,
      legalRepresentativesBackendCount: undefined,
      legalRepresentatives: undefined,
      beneficialOwners: undefined,
      hasUserLoggedIn: undefined,

      hasUserInvited: undefined,
      classificationReasons: undefined,
      clientQuestions: undefined,
      waiverSummary: undefined,
      identificationSummary: undefined,
    });
  }
}

function getClassificationReasons(dto: ClientDetailsReadDTO): OnboardingClassificationReason[] | undefined {
  return (dto.classificationReasons as OnboardingClassificationReason[]) ??
    ((dto as any).classification_reasons as OnboardingClassificationReason[]) ??
    undefined;
}

function getClientQuestions(dto: ClientDetailsReadDTO): OnboardingClientQuestions | undefined {
  const questions = (dto.clientQuestions as OnboardingClientQuestions) ?? ((dto as any).client_questions as OnboardingClientQuestions);
  if (!questions) {
    return undefined;
  }
  return {
    understandRisk: questions.understandRisk ?? (questions as any).understand_risk,
    understandRiskClosedFunds:
      questions.understandRiskClosedFunds ?? (questions as any).understand_risk_closed_funds,
    overallStatus: questions.overallStatus ?? (questions as any).overall_status,
    overallCompleted: questions.overallCompleted ?? (questions as any).overall_completed,
  };
}

function getWaiverSummary(dto: ClientDetailsReadDTO): OnboardingWaiverSummary | undefined {
  const waiverSummary =
    (dto.waiverSummary as OnboardingWaiverSummary) ?? ((dto as any).waiver_summary as OnboardingWaiverSummary);
  if (!waiverSummary) {
    return undefined;
  }
  return {
    ...waiverSummary,
    completedAt: waiverSummary.completedAt ?? (waiverSummary as any).completed_at,
  };
}

function getIdentificationSummary(dto: ClientDetailsReadDTO): OnboardingIdentificationSummary | undefined {
  const identificationSummary =
    (dto.identificationSummary as OnboardingIdentificationSummary) ??
    ((dto as any).identification_summary as OnboardingIdentificationSummary);
  if (!identificationSummary) {
    return undefined;
  }
  return {
    ...identificationSummary,
    completedAt: identificationSummary.completedAt ?? (identificationSummary as any).completed_at,
  };
}

function flattenContacts(prefix: ContactPrefix, contacts: ContactCopy[]): {} {
  const dto = {};

  contacts.forEach((contact, index) => {
    dto[`${prefix}N${index}Address`] = contact.address;
    dto[`${prefix}N${index}Email`] = contact.email;
    dto[`${prefix}N${index}FirstName`] = contact.firstName;
    dto[`${prefix}N${index}IsPep`] = contact.isPep;
    dto[`${prefix}N${index}LastName`] = contact.lastName;
    dto[`${prefix}N${index}Phone`] = contact.phone;
    dto[`${prefix}N${index}Salutation`] = contact.salutation?.codename ?? null;
    dto[`${prefix}N${index}Title`] = contact.title?.codename ?? null;
    dto[`${prefix}N${index}Type`] = contact.type;
    dto[`${prefix}N${index}SignedDeclaration`] = contact.signedDeclaration as any;
    dto[`${prefix}N${index}IdCardScan`] = contact.idCardScan as any;
  });

  return dto;
}

function unflattenContacts(prefix: ContactPrefix, dto: ClientDetailsReadDTO, collectionSize: number): ContactCopy[] {
  const suffixes: ContactSuffix[] = [
    'FirstName',
    'IsPep',
    'LastName',
    'Address',
    'Email',
    'Mobile',
    'Phone',
    'Salutation',
    'Title',
    'IdCardScan',
    'SignedDeclaration',
  ];
  const collection = new Array(collectionSize);

  Object.keys(dto).forEach((key) => {
    if (key.startsWith(prefix) && suffixes.some((v) => key.endsWith(v))) {
      // Retrieve index in collection from key name. Keys are in '{prefix}N{0}{suffix}' form.
      // As number after 'N' can be of any length, use parseInt() ability to handle strings as an input.
      // `prefix.length + 1` is used to start parsing just after 'N' separator.
      const index: number = parseInt(key.substring(prefix.length + 1, key.length), 10);

      collection[index] = ContactCopy.create({
        address: dto[`${prefix}N${index}Address`] as string,
        email: dto[`${prefix}N${index}Email`] as string,
        firstName: dto[`${prefix}N${index}FirstName`] as string,
        isPep: dto[`${prefix}N${index}IsPep`] as boolean,
        lastName: dto[`${prefix}N${index}LastName`] as string,
        phone: dto[`${prefix}N${index}Phone`] as string,
        salutation: ValueObject.create(dto[`${prefix}N${index}Salutation`] as ValueObjectDTO),
        title: ValueObject.create(dto[`${prefix}N${index}Title`] as ValueObjectDTO),
        type: dto[`${prefix}N${index}Type`] as ContactTypeDTO,
      });
    }
  });

  return collection;
}

/**
 * Build an object of contacts to be deleted.
 *
 * To delete an item in a flattened backend collection `{ {prefix}N{0}Delete: true }` field(s) need to be passed in the request payload.
 */
function buildDeletedContacts(prefix: ContactPrefix, count: number, backendCount: number): {} {
  const deletedContacts = {};

  if (backendCount <= count) {
    return deletedContacts;
  }

  // needs to be filled with something so forEach() is invoked
  new Array(backendCount - count).fill('Thanks, Django!').forEach((_, index) => {
    index = index + count;
    deletedContacts[`${prefix}N${index}Delete`] = true;
  });

  return deletedContacts;
}
